/**
 * WorkbenchHeader Component - Responsive Collapsible Version
 * Combines playlist header with energy flow visualization
 * Uses container queries for responsive design
 */

import React, { useState, useRef, useCallback, useMemo, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { useDroppable } from '@dnd-kit/core';
import { createLogger } from '@/lib/logger';
import {
  Music2,
  Sparkles,
  Filter,
  MoreVertical,
  Clock,
  User,
  Play,
  Pause,
  Radio,
  ChevronDown,
  ChevronUp,
  Shuffle,
  BarChart3,
  Zap,
  X,
  RefreshCw,
  Plus,
  FileDown,
  Copy,
  Loader2,
  FileText,
  Edit2,
  GitMerge,
  UserX,
  Music,
  Scissors,
  ShuffleIcon,
  Users,
  ClipboardPaste,
} from 'lucide-react';
import type { PlaylistDetail, Track, TrackWithInstanceId, AudioFeatures, PlaylistImportResponse, ReorderStrategyType } from '../../api/types';
import { REORDER_STRATEGIES } from '../../api/types';

// Smart action types
export type SmartActionType = 'smart_juggle' | 'ask_ai';

export const SMART_ACTIONS = [
  {
    id: 'smart_juggle' as const,
    name: 'Smart Juggle',
    description: 'Intelligently shuffle and optimize track placement',
  },
  {
    id: 'ask_ai' as const,
    name: 'Ask AI',
    description: 'Get AI-powered track suggestions for your playlist',
  },
] as const;
import { formatDuration } from '../../lib/audio-features';
import { WorkbenchActionBar } from './WorkbenchActionBar';
import { useWorkbenchStore, type WorkbenchCapabilities } from '../../stores/workbench';
import { useSetOpsStore } from '../../stores/setops';
import { useSequencerStore } from '../../stores/sequencer';
import { useUIStore } from '../../stores/ui';
import { ImportResultsModal } from './ImportResultsModal';
import { DescriptionEditModal } from './DescriptionEditModal';
import { usePlayerControls, usePlaybackState, useAddTracks } from '../../api/hooks';
import { usePlayerUIStore } from '../../stores/player';
import { usePlaybackQueueStore } from '../../stores/playbackQueue';
import { useSelectionStore } from '../../stores/selection';
import { buildSmartPlaybackParams } from '../../lib/keyboard-helpers';
import { useClipboardStore } from '../../stores/clipboard';
import { toast } from '../ui/Toast';

// Lazy load the chart for performance
const EnergyFlowChart = lazy(() => import('./EnergyFlowChart'));

// Create namespaced logger
const log = createLogger({ namespace: 'WorkbenchHeader' });

interface WorkbenchHeaderProps {
  playlist: PlaylistDetail;
  tracks: TrackWithInstanceId[];
  audioFeaturesMap: Map<string, AudioFeatures>;
  capabilities: WorkbenchCapabilities;
  onFilterClick?: () => void;
  onSmartActionClick?: (action: SmartActionType) => void;
  onIntelligentOrderClick?: (strategy: ReorderStrategyType) => void;
  onSequencerClick?: () => void;
}

// Persist state key
const STORAGE_KEY = 'splister-header-preferences';

interface HeaderPreferences {
  isCollapsed: boolean;
  chartHeight: number;
}

function loadPreferences(): HeaderPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // Ignore localStorage errors
  }
  return { isCollapsed: false, chartHeight: 120 };
}

function savePreferences(prefs: HeaderPreferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore localStorage errors
  }
}

export default function WorkbenchHeader({
  playlist,
  tracks,
  audioFeaturesMap,
  capabilities,
  onFilterClick,
  onSmartActionClick,
  onIntelligentOrderClick,
  onSequencerClick,
}: WorkbenchHeaderProps) {
  // Load persisted preferences
  const [preferences, setPreferences] = useState<HeaderPreferences>(loadPreferences);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Import/Export state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importMode, setImportMode] = useState<'add' | 'replace'>('add');
  const [importText, setImportText] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [importResults, setImportResults] = useState<PlaylistImportResponse | null>(null);
  const [showResults, setShowResults] = useState(false);

  // Description edit modal state
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

  // Remove Artist modal state
  const [isRemoveArtistModalOpen, setIsRemoveArtistModalOpen] = useState(false);

  // Workbench store
  const setActivePlaylist = useWorkbenchStore((state) => state.setActivePlaylist);
  const setTracks = useWorkbenchStore((state) => state.setTracks);
  const markClean = useWorkbenchStore((state) => state.markClean);
  const activePlaylist = useWorkbenchStore((state) => state.activePlaylist);

  // Clipboard store
  const clipboardStore = useClipboardStore();
  const hasClipboardTracks = clipboardStore.hasTracks();
  const clipboardTrackCount = clipboardStore.getTrackCount();

  // Add tracks mutation
  const addTracksMutation = useAddTracks();

  // UI store for discovery panel control
  const { setRightPanelTab, setDiscoveryPanelOpen } = useUIStore();

  // SetOps drop zone - only active when playlist is dragged AND sequencer is closed AND no unsaved results
  const { isDragging: isPlaylistDragging, resultTracks } = useSetOpsStore();
  const { isOpen: isSequencerOpen } = useSequencerStore();
  const canDropSetOps = isPlaylistDragging && !isSequencerOpen && (!resultTracks || resultTracks.length === 0);

  const { setNodeRef: setSetOpsDropRef, isOver: isSetOpsOver } = useDroppable({
    id: 'workbench-header-setops',
    data: { type: 'setops-target' },
  });

  // Destructure for convenience
  const { isCollapsed: isManuallyCollapsed, chartHeight } = preferences;

  // Update and persist preferences
  const updatePreferences = useCallback((updates: Partial<HeaderPreferences>) => {
    setPreferences((prev) => {
      const next = { ...prev, ...updates };
      savePreferences(next);
      return next;
    });
  }, []);

  // Toggle menu with position calculation
  const handleToggleMenu = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!showActionsMenu) {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setShowActionsMenu(!showActionsMenu);
  }, [showActionsMenu]);

  // Import modal handlers
  const handleOpenImportModal = (mode: 'add' | 'replace') => {
    setImportMode(mode);
    setImportText('');
    setShowActionsMenu(false);
    setIsImportModalOpen(true);
  };

  const handleImport = async () => {
    if (!importText.trim()) return;

    setIsImporting(true);
    try {
      const { playlistService } = await import('../../api/services/playlists');
      const result = await playlistService.import(playlist.id, {
        text: importText,
        mode: importMode,
      });

      setImportResults(result);
      setIsImportModalOpen(false);
      setShowResults(true);

      // Skip refresh for Liked Songs (uses different endpoint)
      if (playlist.id !== 'liked-songs') {
        const updatedPlaylist = await playlistService.get(playlist.id);
        setActivePlaylist(updatedPlaylist);
        markClean();
      }
      setImportText('');
    } catch (error) {
      log.error('Failed to import tracks:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // Export handlers
  const handleExportPlain = async () => {
    if (tracks.length === 0) return;

    const plainText = tracks
      .map((track) => {
        const artists = track.artists.map((a) => a.name).join(', ');
        return `${artists} - ${track.name}`;
      })
      .join('\n');

    try {
      await navigator.clipboard.writeText(plainText);
      log.debug('Copied to clipboard (plain):', tracks.length, 'tracks');
      setShowActionsMenu(false);
    } catch (error) {
      log.error('Failed to copy to clipboard:', error);
    }
  };

  const handleExportCSV = async () => {
    if (tracks.length === 0) return;

    const headers = 'Artist,Track,Album,Duration (ms)';
    const rows = tracks.map((track) => {
      const artists = track.artists.map((a) => a.name).join('; ');
      const trackName = track.name.replace(/"/g, '""');
      const albumName = track.album.name.replace(/"/g, '""');
      return `"${artists}","${trackName}","${albumName}",${track.durationMs}`;
    });

    const csvText = [headers, ...rows].join('\n');

    try {
      await navigator.clipboard.writeText(csvText);
      log.debug('Copied to clipboard (CSV):', tracks.length, 'tracks');
      setShowActionsMenu(false);
    } catch (error) {
      log.error('Failed to copy to clipboard:', error);
    }
  };

  const handleCopyPrompt = async () => {
    setIsGeneratingPrompt(true);
    setShowActionsMenu(false);

    try {
      const { playlistService } = await import('../../api/services/playlists');
      const result = await playlistService.generatePrompt(playlist.id);
      await navigator.clipboard.writeText(result.prompt);
      log.debug(`Copied ${result.detectedType} prompt to clipboard`);
    } catch (error) {
      log.error('Failed to generate prompt:', error);
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  // Quick Actions handlers
  const handleDeduplicate = () => {
    setShowActionsMenu(false);
    // Remove duplicates based on URI (most reliable unique identifier)
    const seen = new Set<string>();
    const deduplicated = tracks.filter((track) => {
      if (seen.has(track.uri)) {
        return false;
      }
      seen.add(track.uri);
      return true;
    });

    const removedCount = tracks.length - deduplicated.length;
    if (removedCount > 0) {
      setTracks(deduplicated);
      log.debug(`Removed ${removedCount} duplicate tracks`);
    } else {
      log.debug('No duplicates found');
    }
  };

  const handleRemoveArtist = () => {
    setShowActionsMenu(false);
    setIsRemoveArtistModalOpen(true);
  };

  const handleRemoveArtistConfirm = (artistName: string) => {
    const filtered = tracks.filter((track) => {
      return !track.artists.some((artist) => artist.name === artistName);
    });

    const removedCount = tracks.length - filtered.length;
    if (removedCount > 0) {
      setTracks(filtered);
      log.debug(`Removed ${removedCount} tracks by ${artistName}`);
    }
    setIsRemoveArtistModalOpen(false);
  };

  const handleRemoveLiveVersions = () => {
    setShowActionsMenu(false);
    const livePatterns = [
      /\blive\b/i,
      /\blive at\b/i,
      /\blive from\b/i,
      /\[live\]/i,
      /\(live\)/i,
    ];

    const filtered = tracks.filter((track) => {
      return !livePatterns.some((pattern) => pattern.test(track.name));
    });

    const removedCount = tracks.length - filtered.length;
    if (removedCount > 0) {
      setTracks(filtered);
      log.debug(`Removed ${removedCount} live versions`);
    } else {
      log.debug('No live versions found');
    }
  };

  const handleRemoveShortTracks = () => {
    setShowActionsMenu(false);
    const minDuration = 90000; // 90 seconds in milliseconds
    const filtered = tracks.filter((track) => track.durationMs >= minDuration);

    const removedCount = tracks.length - filtered.length;
    if (removedCount > 0) {
      setTracks(filtered);
      log.debug(`Removed ${removedCount} short tracks (< 90s)`);
    } else {
      log.debug('No short tracks found');
    }
  };

  const handleRandomShuffle = () => {
    setShowActionsMenu(false);
    const shuffled = [...tracks].sort(() => Math.random() - 0.5);
    setTracks(shuffled);
    log.debug('Tracks shuffled randomly');
  };

  const handleSeparateArtists = () => {
    setShowActionsMenu(false);
    // Simple algorithm: try to avoid consecutive tracks by same artist
    const separated = [...tracks];
    let swapsMade = 0;

    for (let i = 0; i < separated.length - 1; i++) {
      const currentTrack = separated[i];
      const nextTrack = separated[i + 1];

      if (!currentTrack || !nextTrack) continue;

      // Check if same artist
      const currentArtists = currentTrack.artists.map((a) => a.id);
      const nextArtists = nextTrack.artists.map((a) => a.id);
      const hasSameArtist = currentArtists.some((id) => nextArtists.includes(id));

      if (hasSameArtist) {
        // Try to find a different artist track to swap with
        for (let j = i + 2; j < separated.length; j++) {
          const candidateTrack = separated[j];
          if (!candidateTrack) continue;

          const candidateArtists = candidateTrack.artists.map((a) => a.id);
          const candidateHasSameArtist = currentArtists.some((id) => candidateArtists.includes(id));

          if (!candidateHasSameArtist) {
            // Swap
            [separated[i + 1], separated[j]] = [candidateTrack, nextTrack];
            swapsMade++;
            break;
          }
        }
      }
    }

    setTracks(separated);
    log.debug(`Separated artists with ${swapsMade} swaps`);
  };

  // Handle paste from clipboard
  const handlePasteFromClipboard = async () => {
    // Validate active playlist
    if (!activePlaylist) {
      toast.error('Please select a playlist first');
      return;
    }

    // Validate clipboard has tracks
    if (!hasClipboardTracks) {
      toast.error('Clipboard is empty');
      return;
    }

    // Check if playlist supports adding tracks
    if (!capabilities.canAddRemove) {
      toast.error('This playlist does not support adding tracks');
      return;
    }

    const tracksToAdd = clipboardStore.tracks;
    const uris = tracksToAdd.map((track) => track.uri);

    try {
      // Show loading toast
      const loadingToastId = toast.loading(`Adding ${clipboardTrackCount} track${clipboardTrackCount > 1 ? 's' : ''}...`);

      // Add tracks to playlist at the end
      await addTracksMutation.mutateAsync({
        playlistId: activePlaylist.id,
        data: {
          uris,
          // position: undefined means append to end
        },
      });

      // Dismiss loading toast
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
      }

      // Show success toast
      toast.success(
        `Added ${clipboardTrackCount} track${clipboardTrackCount > 1 ? 's' : ''} to ${activePlaylist.name}`
      );

      // Clear clipboard after successful paste
      clipboardStore.clearTracks();

      log.debug(`Pasted ${clipboardTrackCount} tracks to playlist ${activePlaylist.id}`);
    } catch (error) {
      log.error('Failed to paste tracks:', error);
      toast.error('Failed to add tracks. Please try again.', {
        action: {
          label: 'Retry',
          onClick: handlePasteFromClipboard,
        },
      });
    }
  };

  // Safe image access
  const coverUrl = playlist.images?.[playlist.images.length - 1]?.url ?? null;
  const totalDuration = tracks.reduce((acc, t) => acc + t.durationMs, 0);
  const avgEnergy = calculateAverageEnergy(tracks, audioFeaturesMap);

  const toggleCollapse = () => {
    updatePreferences({ isCollapsed: !isManuallyCollapsed });
  };

  const {
    isPlayerActive,
    setShowDeviceSelector,
    setIsPlayerActive,
    setPendingPlayAction,
  } = usePlayerUIStore();

  // Import play/pause/queue for play button
  const { play, pause } = usePlayerControls();
  const { data: playbackState } = usePlaybackState({ enabled: isPlayerActive });
  const isPlaying = playbackState?.isPlaying ?? false;
  const currentTrack = playbackState?.item ?? null;

  // Check if currently playing first track from this playlist
  const isPlayingThisPlaylist = currentTrack && tracks.length > 0 && tracks[0]?.id === currentTrack.id;
  const playbackQueue = usePlaybackQueueStore();
  const { previewMode, setHookTimer, clearHookTimer } = playbackQueue;
  const { getSelectedIds, selectOnly, selectedTrackIds } = useSelectionStore();

  // Get the index of the first selected track (for two-way sync with EnergyFlowChart)
  const selectedTrackIndex = useMemo(() => {
    if (selectedTrackIds.size === 0) return null;
    const firstSelectedId = Array.from(selectedTrackIds)[0];
    const index = tracks.findIndex((t) => t.instanceId === firstSelectedId);
    return index >= 0 ? index : null;
  }, [selectedTrackIds, tracks]);

  // Handle track click from EnergyFlowChart - select track and scroll to it
  const handleChartTrackClick = useCallback((_trackId: string, index: number) => {
    // Find the track by index to get its instanceId
    const track = tracks[index];
    if (track?.instanceId) {
      selectOnly(track.instanceId);
    }
  }, [tracks, selectOnly]);

  /**
   * Play hookmode track with auto-advance timer
   * Same logic as TrackRow
   */
  const playHookmodeTrack = (hookItem: { uri: string; durationMs: number }) => {
    const hookStart = Math.floor(hookItem.durationMs / 2 - 7500);
    const hookDuration = 15000;

    clearHookTimer();

    play.mutate({
      uris: [hookItem.uri],
      positionMs: Math.max(0, hookStart),
    });

    const timerId = setTimeout(() => {
      const nextItem = playbackQueue.moveNext();
      if (nextItem) {
        playHookmodeTrack(nextItem);
      } else {
        pause.mutate();
      }
    }, hookDuration);

    setHookTimer(timerId);
  };

  /**
   * Handle play button click on playlist cover
   * Populates playback queue based on selection, then starts playback
   * Respects hookmode setting
   */
  const handlePlayPlaylist = () => {
    if (tracks.length === 0) return;

    const selectedIds = getSelectedIds();
    const hasSelection = selectedIds.length > 0;

    // Build queue based on selection
    let queueTracks: Track[];
    let source: 'selection' | 'playlist';

    if (hasSelection) {
      queueTracks = tracks.filter((t) => selectedIds.includes(t.instanceId));
      source = 'selection';
    } else {
      queueTracks = tracks;
      source = 'playlist';
    }

    const queueItems = queueTracks.map((t) => ({
      uri: t.uri,
      id: t.id,
      durationMs: t.durationMs,
    }));

    // Smart playback: use contextUri when playing full playlist in original order
    // Selection always uses uris[] since it's a subset
    const isUnchanged = useWorkbenchStore.getState().isOrderUnchanged();
    const canUseContext = !hasSelection && isUnchanged;
    const playbackParams = buildSmartPlaybackParams(
      playlist.uri,
      0,
      queueItems.map((i) => i.uri),
      canUseContext
    );

    // Check if device is available before playing
    if (!playbackState?.device) {
      setIsPlayerActive(true);
      setPendingPlayAction(() => {
        if (previewMode === 'hook') {
          playbackQueue.setQueue(queueItems, 0, source, false);
          const startItem = queueItems[0];
          if (startItem) {
            playHookmodeTrack(startItem);
          }
        } else {
          playbackQueue.setQueue(queueItems, 0, source, playbackParams.isContextMode);
          play.mutate(playbackParams);
        }
      });
      setShowDeviceSelector(true);
      return;
    }

    if (previewMode === 'hook') {
      // Hookmode: we manage queue ourselves
      playbackQueue.setQueue(queueItems, 0, source, false);
      const startItem = queueItems[0];
      if (startItem) {
        playHookmodeTrack(startItem);
      }
    } else {
      // Normal mode: use smart playback
      playbackQueue.setQueue(queueItems, 0, source, playbackParams.isContextMode);
      play.mutate(playbackParams);
    }
  };

  /**
   * Handle pause button click on playlist cover
   */
  const handlePause = () => {
    pause.mutate();
    clearHookTimer();
  };

  /**
   * Handle Smart Slot action click
   * Opens Discovery Panel and activates Recs tab to show recommendations
   */
  const handleSmartSlotClick = (action: SmartActionType) => {
    // Trigger original smart action
    onSmartActionClick?.(action);

    // Open Discovery Panel and switch to Recs tab
    setDiscoveryPanelOpen(true);
    setRightPanelTab('recs');
  };

  // Combine refs for the container
  const setRefs = useCallback((node: HTMLDivElement | null) => {
    // Set both refs
    (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    setSetOpsDropRef(node);
  }, [setSetOpsDropRef]);

  return (
    <div
      ref={setRefs}
      className="@container border-b border-zinc-800 relative"
      role="banner"
      aria-label="Playlist header"
    >
      {/* Action Bar - Shows for SetOps results, filter preview, or pending changes */}
      <WorkbenchActionBar />

      {/* Main Header Content */}
      <div
        id="header-content"
        className={`
          relative overflow-hidden z-10
          transition-all duration-300 ease-out
          ${isManuallyCollapsed ? 'max-h-[140px]' : 'max-h-[500px]'}
        `}
      >
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-800/50 to-transparent pointer-events-none" />

        {/* Responsive Layout Container */}
        <div className="relative p-[var(--space-md)]">
          {/* === EXPANDED LAYOUT (Container >= 600px) === */}
          <div className="hidden @[600px]:block">
            <div className="flex gap-4">
              {/* Cover Art */}
              <div className="flex-shrink-0 relative group/cover w-20 h-20">
                {coverUrl ? (
                  <img
                    src={coverUrl}
                    alt=""
                    className="w-20 h-20 rounded-lg shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-zinc-800 flex items-center justify-center">
                    <Music2 className="w-8 h-8 text-zinc-600" aria-hidden="true" />
                  </div>
                )}
                {/* Play/Pause Overlay */}
                <button
                  onClick={isPlayingThisPlaylist && isPlaying ? handlePause : handlePlayPlaylist}
                  className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover/cover:opacity-100 focus:opacity-100 transition-opacity rounded-lg"
                  aria-label={isPlayingThisPlaylist && isPlaying ? `Pause ${playlist.name}` : `Play ${playlist.name}`}
                >
                  {isPlayingThisPlaylist && isPlaying ? (
                    <Pause className="w-8 h-8 text-white fill-white" aria-hidden="true" />
                  ) : (
                    <Play className="w-8 h-8 text-white fill-white" aria-hidden="true" />
                  )}
                </button>
              </div>

              {/* Metadata Column */}
              <div className="flex-1 min-w-0 flex flex-col justify-center group">
                {/* Title Row */}
                <div className="flex items-center gap-2">
                  <h1 className="text-[length:var(--text-3xl)] font-bold text-zinc-100 truncate leading-tight">
                    {playlist.name}
                  </h1>
                  <button
                    onClick={() => setIsDescriptionModalOpen(true)}
                    className="p-1.5 rounded-lg bg-zinc-800/50 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors opacity-0 group-hover:opacity-100"
                    title="Edit playlist details"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Badges and Stats - Combined Row */}
                <div className="flex items-center gap-3 mt-1.5 text-[length:var(--text-xs)] text-zinc-400">
                  {/* Badges */}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-800">
                    {playlist.public ? 'Public' : 'Private'}
                  </span>
                  {avgEnergy !== null && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-900/40 text-emerald-400">
                      <BarChart3 className="w-3 h-3" aria-hidden="true" />
                      Analyzed
                    </span>
                  )}

                  {/* Divider */}
                  <span className="text-zinc-700">•</span>

                  {/* Stats */}
                  <span className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" aria-hidden="true" />
                    <span>{playlist.owner.displayName}</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                    <span>{formatDuration(totalDuration)}</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Music2 className="w-3.5 h-3.5" aria-hidden="true" />
                    <span>{tracks.length} tracks</span>
                  </span>
                  {avgEnergy !== null && (
                    <span className="flex items-center gap-1.5 text-emerald-400">
                      <Zap className="w-3.5 h-3.5" aria-hidden="true" />
                      <span>{(avgEnergy * 100).toFixed(0)}%</span>
                    </span>
                  )}
                </div>

                {/* Description (truncated) */}
                {playlist.description && (
                  <p className="text-[length:var(--text-sm)] text-zinc-500 italic line-clamp-1 mt-2">
                    {playlist.description}
                  </p>
                )}
              </div>

              {/* Actions Column - Full */}
              <div className="flex-shrink-0 flex flex-col gap-2">
                {/* Primary Actions Row */}
                <div className="flex items-center gap-2">
                  {hasClipboardTracks && (
                    <ActionButton
                      icon={ClipboardPaste}
                      label={`Paste ${clipboardTrackCount}`}
                      variant="primary"
                      onClick={handlePasteFromClipboard}
                    />
                  )}
                  {capabilities.canAddRemove && (
                    <SplitActionButton
                      icon={Sparkles}
                      label="Smart Slot"
                      variant="info"
                      onDefaultClick={() => handleSmartSlotClick('smart_juggle')}
                      onActionSelect={handleSmartSlotClick}
                      actions={SMART_ACTIONS}
                    />
                  )}
                  {capabilities.canReorder && (
                    <SplitActionButton
                      icon={Shuffle}
                      label="Mix"
                      variant="secondary"
                      onDefaultClick={() => onIntelligentOrderClick?.('full_dj_mode')}
                      onStrategySelect={onIntelligentOrderClick}
                    />
                  )}
                  <ActionButton
                    icon={Radio}
                    label="Sequencer"
                    variant="accent"
                    onClick={onSequencerClick}
                  />
                </div>
                {/* Secondary Actions Row */}
                <div className="flex items-center gap-4 justify-end">
                  <IconButton
                    icon={Filter}
                    onClick={onFilterClick}
                    aria-label="Open filter panel"
                  />
                  <MenuButton
                    onClick={handleToggleMenu}
                    isExpanded={showActionsMenu}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* === COMPACT LAYOUT (Container < 600px) === */}
          <div className="block @[600px]:hidden">
            <div className="flex items-center gap-3">
              {/* Mini Cover */}
              <div className="flex-shrink-0 relative group/cover w-10 h-10">
                {coverUrl ? (
                  <img
                    src={coverUrl}
                    alt=""
                    className="w-10 h-10 rounded-md shadow object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-zinc-800 flex items-center justify-center">
                    <Music2 className="w-5 h-5 text-zinc-600" aria-hidden="true" />
                  </div>
                )}
                {/* Play/Pause Overlay */}
                <button
                  onClick={isPlayingThisPlaylist && isPlaying ? handlePause : handlePlayPlaylist}
                  className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover/cover:opacity-100 focus:opacity-100 transition-opacity rounded-md"
                  aria-label={isPlayingThisPlaylist && isPlaying ? `Pause ${playlist.name}` : `Play ${playlist.name}`}
                >
                  {isPlayingThisPlaylist && isPlaying ? (
                    <Pause className="w-5 h-5 text-white fill-white" aria-hidden="true" />
                  ) : (
                    <Play className="w-5 h-5 text-white fill-white" aria-hidden="true" />
                  )}
                </button>
              </div>

              {/* Compact Metadata */}
              <div className="flex-1 min-w-0 group">
                <div className="flex items-center gap-2">
                  <h1 className="text-[length:var(--text-lg)] font-semibold text-zinc-100 truncate">
                    {playlist.name}
                  </h1>
                  <button
                    onClick={() => setIsDescriptionModalOpen(true)}
                    className="p-1.5 rounded-lg bg-zinc-800/50 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors opacity-0 group-hover:opacity-100"
                    title="Edit playlist details"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-[length:var(--text-xs)] text-zinc-500">
                  {/* Badges */}
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">
                    {playlist.public ? 'Public' : 'Private'}
                  </span>
                  {avgEnergy !== null && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-400">
                      <BarChart3 className="w-2.5 h-2.5" aria-hidden="true" />
                    </span>
                  )}

                  {/* Divider */}
                  <span className="text-zinc-700">•</span>

                  {/* Stats */}
                  <span className="flex items-center gap-1">
                    <Music2 className="w-3 h-3" aria-hidden="true" />
                    <span>{tracks.length}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" aria-hidden="true" />
                    <span>{formatDuration(totalDuration)}</span>
                  </span>
                  {avgEnergy !== null && (
                    <span className="flex items-center gap-1 text-emerald-400">
                      <Zap className="w-3 h-3" aria-hidden="true" />
                      <span>{(avgEnergy * 100).toFixed(0)}%</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Compact Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleCollapse}
                  className="p-2 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
                  aria-expanded={!isManuallyCollapsed}
                  aria-controls="header-content"
                  aria-label={isManuallyCollapsed ? 'Expand header' : 'Collapse header'}
                >
                  {isManuallyCollapsed ? (
                    <ChevronDown className="w-4 h-4" aria-hidden="true" />
                  ) : (
                    <ChevronUp className="w-4 h-4" aria-hidden="true" />
                  )}
                </button>
                {hasClipboardTracks && (
                  <IconButton
                    icon={ClipboardPaste}
                    size="sm"
                    onClick={handlePasteFromClipboard}
                    aria-label={`Paste ${clipboardTrackCount} tracks`}
                  />
                )}
                {capabilities.canAddRemove && (
                  <IconButton
                    icon={Sparkles}
                    size="sm"
                    onClick={() => handleSmartSlotClick('smart_juggle')}
                    aria-label="Smart Slot"
                  />
                )}
                <MenuButton
                  onClick={handleToggleMenu}
                  isExpanded={showActionsMenu}
                  size="sm"
                />
              </div>
            </div>

            {/* Expanded Content (when not collapsed) */}
            {!isManuallyCollapsed && (
              <div className="mt-3 pt-3 border-t border-zinc-800/50">
                <div className="flex flex-wrap gap-2">
                  {hasClipboardTracks && (
                    <ActionButton
                      icon={ClipboardPaste}
                      label={`Paste ${clipboardTrackCount}`}
                      variant="primary"
                      size="sm"
                      onClick={handlePasteFromClipboard}
                    />
                  )}
                  {capabilities.canAddRemove && (
                    <SplitActionButton
                      icon={Sparkles}
                      label="Smart Slot"
                      variant="info"
                      size="sm"
                      onDefaultClick={() => handleSmartSlotClick('smart_juggle')}
                      onActionSelect={handleSmartSlotClick}
                      actions={SMART_ACTIONS}
                    />
                  )}
                  {capabilities.canReorder && (
                    <SplitActionButton
                      icon={Shuffle}
                      label="Mix"
                      variant="secondary"
                      size="sm"
                      onDefaultClick={() => onIntelligentOrderClick?.('full_dj_mode')}
                      onStrategySelect={onIntelligentOrderClick}
                    />
                  )}
                  <ActionButton
                    icon={Radio}
                    label="Seq"
                    variant="accent"
                    size="sm"
                    onClick={onSequencerClick}
                  />
                  <IconButton
                    icon={Filter}
                    size="sm"
                    onClick={onFilterClick}
                    aria-label="Open filter panel"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Energy Flow Chart - Outside collapsible area so it's always visible */}
      <div
        className={`
          px-[var(--space-md)] pb-[var(--space-md)]
          transition-all duration-300
        `}
      >
        <Suspense fallback={<ChartSkeleton height={isManuallyCollapsed ? 12 : chartHeight} />}>
          <EnergyFlowChart
            tracks={tracks}
            audioFeaturesMap={audioFeaturesMap}
            mode={isManuallyCollapsed ? 'heatmap' : 'full'}
            height={isManuallyCollapsed ? 12 : chartHeight}
            resizable={!isManuallyCollapsed}
            onHeightChange={(h) => updatePreferences({ chartHeight: h })}
            onTrackClick={handleChartTrackClick}
            selectedTrackIndex={selectedTrackIndex}
          />
        </Suspense>
      </div>

      {/* Collapse Toggle (visible only in expanded container) */}
      <button
        onClick={toggleCollapse}
        className="hidden @[600px]:flex absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2
                   items-center justify-center w-6 h-6 rounded-full
                   bg-zinc-800 border border-zinc-700 text-zinc-400
                   hover:bg-zinc-700 hover:text-zinc-200 transition-colors z-10"
        aria-expanded={!isManuallyCollapsed}
        aria-controls="header-content"
        aria-label={isManuallyCollapsed ? 'Expand header' : 'Collapse header'}
      >
        {isManuallyCollapsed ? (
          <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
        ) : (
          <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
        )}
      </button>

      {/* SetOps drop overlay - shown when dragging playlist over header */}
      {canDropSetOps && (
        <div
          className={`
            absolute inset-0 z-50 rounded-lg border-2 border-dashed
            flex items-center justify-center transition-all pointer-events-none
            ${isSetOpsOver
              ? 'bg-emerald-500/20 border-emerald-500'
              : 'bg-zinc-800/50 border-zinc-600'
            }
          `}
        >
          <div className="text-center p-8">
            <div className={`
              text-lg font-medium transition-colors
              ${isSetOpsOver ? 'text-emerald-400' : 'text-zinc-400'}
            `}>
              {isSetOpsOver
                ? 'Release to combine playlists'
                : 'Drop here to combine playlists'
              }
            </div>
            <div className="text-sm text-zinc-500 mt-2">
              Choose union, intersection, difference, or symmetric difference
            </div>
          </div>
        </div>
      )}

      {/* Portal-based Actions Menu */}
      {showActionsMenu && typeof document !== 'undefined' &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setShowActionsMenu(false)}
            />
            {/* Menu */}
            <ActionsDropdown
              onClose={() => setShowActionsMenu(false)}
              onFilterClick={onFilterClick}
              onImportReplace={() => handleOpenImportModal('replace')}
              onImportAdd={() => handleOpenImportModal('add')}
              onExportPlain={handleExportPlain}
              onExportCSV={handleExportCSV}
              onCopyPrompt={handleCopyPrompt}
              isGeneratingPrompt={isGeneratingPrompt}
              tracksCount={tracks.length}
              onDeduplicate={handleDeduplicate}
              onRemoveArtist={handleRemoveArtist}
              onRemoveLiveVersions={handleRemoveLiveVersions}
              onRemoveShortTracks={handleRemoveShortTracks}
              onRandomShuffle={handleRandomShuffle}
              onSeparateArtists={handleSeparateArtists}
              style={{
                top: `${menuPosition.top}px`,
                right: `${menuPosition.right}px`,
              }}
            />
          </>,
          document.body
        )}

      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-zinc-900 rounded-lg shadow-2xl border border-zinc-800 w-full max-w-2xl mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-emerald-500" />
                <h2 className="text-lg font-semibold text-zinc-100">
                  Import Tracks - {importMode === 'replace' ? 'Replace All' : 'Add to Playlist'}
                </h2>
              </div>
              <button
                onClick={() => setIsImportModalOpen(false)}
                className="text-zinc-400 hover:text-zinc-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-zinc-400 mb-4">
                Paste track listings below, one per line. Format:{' '}
                <code className="bg-zinc-800 px-2 py-1 rounded">Artist - Track</code>
              </p>
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Daft Punk - One More Time&#10;Gorillaz - Feel Good Inc.&#10;The Weeknd - Blinding Lights"
                className="w-full h-64 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none font-mono text-sm"
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800">
              <div className="text-sm text-zinc-400">
                {importMode === 'replace' && (
                  <span className="text-orange-400">
                    Warning: This will replace all tracks in the playlist
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsImportModalOpen(false)}
                  className="px-4 py-2 text-sm text-zinc-300 hover:text-zinc-100 transition-colors"
                  disabled={isImporting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={!importText.trim() || isImporting}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" />
                      Import
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Results Modal */}
      {importResults && (
        <ImportResultsModal
          isOpen={showResults}
          onClose={() => {
            setShowResults(false);
            setImportResults(null);
          }}
          results={importResults}
          mode={importMode}
        />
      )}

      {/* Edit Details Modal */}
      <DescriptionEditModal
        isOpen={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        playlist={playlist}
        onSuccess={(updated) => {
          setActivePlaylist({
            ...playlist,
            name: updated.name,
            description: updated.description,
            public: updated.public,
          });
          if (tracks.length === 0) {
            markClean();
          }
        }}
      />

      {/* Remove Artist Modal */}
      {isRemoveArtistModalOpen && (
        <RemoveArtistModal
          tracks={tracks}
          onClose={() => setIsRemoveArtistModalOpen(false)}
          onConfirm={handleRemoveArtistConfirm}
        />
      )}
    </div>
  );
}

// === Loading Skeleton ===

function ChartSkeleton({ height }: { height: number }) {
  return (
    <div
      className="w-full bg-zinc-900/50 rounded-lg animate-pulse"
      style={{ height }}
      role="progressbar"
      aria-label="Loading chart..."
    />
  );
}

// === Actions Dropdown Menu ===

interface ActionsDropdownProps {
  onClose: () => void;
  onFilterClick?: () => void;
  onImportReplace: () => void;
  onImportAdd: () => void;
  onExportPlain: () => void;
  onExportCSV: () => void;
  onCopyPrompt: () => void;
  isGeneratingPrompt: boolean;
  tracksCount: number;
  style?: React.CSSProperties;
  // Quick Actions
  onDeduplicate: () => void;
  onRemoveArtist: () => void;
  onRemoveLiveVersions: () => void;
  onRemoveShortTracks: () => void;
  onRandomShuffle: () => void;
  onSeparateArtists: () => void;
}

function ActionsDropdown({
  onClose,
  onFilterClick,
  onImportReplace,
  onImportAdd,
  onExportPlain,
  onExportCSV,
  onCopyPrompt,
  isGeneratingPrompt,
  tracksCount,
  style,
  onDeduplicate,
  onRemoveArtist,
  onRemoveLiveVersions,
  onRemoveShortTracks,
  onRandomShuffle,
  onSeparateArtists,
}: ActionsDropdownProps) {
  return (
    <div
      className="fixed w-56 py-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-[9999]"
      style={style}
      role="menu"
      aria-label="Actions menu"
    >
      <button
        className="w-full px-4 py-2.5 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-3 transition-colors"
        onClick={() => {
          onFilterClick?.();
          onClose();
        }}
        role="menuitem"
      >
        <Filter className="w-4 h-4" aria-hidden="true" />
        Filter Tracks
      </button>

      <hr className="my-1 border-zinc-700" />

      {/* Quick Actions Section */}
      <div className="px-4 py-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
        Quick Actions
      </div>

      <button
        onClick={onDeduplicate}
        disabled={tracksCount === 0}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        role="menuitem"
      >
        <GitMerge className="w-4 h-4" />
        Deduplicate
      </button>

      <button
        onClick={onRemoveArtist}
        disabled={tracksCount === 0}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        role="menuitem"
      >
        <UserX className="w-4 h-4" />
        Remove Artist
      </button>

      <button
        onClick={onRemoveLiveVersions}
        disabled={tracksCount === 0}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        role="menuitem"
      >
        <Music className="w-4 h-4" />
        Remove Live Versions
      </button>

      <button
        onClick={onRemoveShortTracks}
        disabled={tracksCount === 0}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        role="menuitem"
      >
        <Scissors className="w-4 h-4" />
        Remove Short Tracks
      </button>

      <button
        onClick={onRandomShuffle}
        disabled={tracksCount === 0}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        role="menuitem"
      >
        <ShuffleIcon className="w-4 h-4" />
        Random Shuffle
      </button>

      <button
        onClick={onSeparateArtists}
        disabled={tracksCount === 0}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        role="menuitem"
      >
        <Users className="w-4 h-4" />
        Separate Artists
      </button>

      <hr className="my-1 border-zinc-700" />

      <button
        onClick={onImportReplace}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
        role="menuitem"
      >
        <RefreshCw className="w-4 h-4" />
        Import - Replace
      </button>

      <button
        onClick={onImportAdd}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
        role="menuitem"
      >
        <Plus className="w-4 h-4" />
        Import - Add
      </button>

      <hr className="my-1 border-zinc-700" />

      <button
        onClick={onExportPlain}
        disabled={tracksCount === 0}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        role="menuitem"
      >
        <FileDown className="w-4 h-4" />
        Export - Plain
      </button>

      <button
        onClick={onExportCSV}
        disabled={tracksCount === 0}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        role="menuitem"
      >
        <FileDown className="w-4 h-4" />
        Export - CSV
      </button>

      <hr className="my-1 border-zinc-700" />

      <button
        onClick={onCopyPrompt}
        disabled={isGeneratingPrompt}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        role="menuitem"
      >
        {isGeneratingPrompt ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
        Copy Prompt
      </button>
    </div>
  );
}

// === Reusable Button Components ===

interface ActionButtonProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  variant: 'primary' | 'secondary' | 'accent' | 'info';
  size?: 'sm' | 'md';
  onClick?: () => void;
}

function ActionButton({ icon: Icon, label, variant, size = 'md', onClick }: ActionButtonProps) {
  const variants = {
    primary: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    secondary: 'bg-purple-600 hover:bg-purple-700 text-white',
    accent: 'bg-orange-600 hover:bg-orange-700 text-white',
    info: 'bg-blue-600 hover:bg-blue-700 text-white',
  };

  const sizes = {
    sm: 'px-2.5 py-1.5 text-[length:var(--text-xs)] gap-1.5',
    md: 'px-3.5 py-2 text-[length:var(--text-sm)] gap-2',
  };

  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center rounded-lg font-medium transition-colors
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900
        ${variants[variant]} ${sizes[size]}
        ${variant === 'primary' ? 'focus:ring-emerald-500' : ''}
        ${variant === 'secondary' ? 'focus:ring-purple-500' : ''}
        ${variant === 'accent' ? 'focus:ring-orange-500' : ''}
        ${variant === 'info' ? 'focus:ring-blue-500' : ''}
      `}
    >
      <Icon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

// === Split Action Button with Dropdown ===

interface SplitActionButtonProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  variant: 'primary' | 'secondary' | 'accent' | 'info';
  size?: 'sm' | 'md';
  onDefaultClick?: () => void;
  onStrategySelect?: (strategy: ReorderStrategyType) => void;
  onActionSelect?: (action: SmartActionType) => void;
  actions?: readonly { id: SmartActionType; name: string; description: string }[];
}

function SplitActionButton({
  icon: Icon,
  label,
  variant,
  size = 'md',
  onDefaultClick,
  onStrategySelect,
  onActionSelect,
  actions,
}: SplitActionButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);

  const variants = {
    primary: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    secondary: 'bg-purple-600 hover:bg-purple-700 text-white',
    accent: 'bg-orange-600 hover:bg-orange-700 text-white',
    info: 'bg-blue-600 hover:bg-blue-700 text-white',
  };

  const dividerColors = {
    primary: 'border-emerald-500',
    secondary: 'border-purple-500',
    accent: 'border-orange-500',
    info: 'border-blue-500',
  };

  const sizes = {
    sm: 'text-[length:var(--text-xs)]',
    md: 'text-[length:var(--text-sm)]',
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleStrategyClick = (strategy: ReorderStrategyType) => {
    setIsOpen(false);
    onStrategySelect?.(strategy);
  };

  const handleActionClick = (action: SmartActionType) => {
    setIsOpen(false);
    onActionSelect?.(action);
  };

  return (
    <>
      <div ref={buttonRef} className="inline-flex rounded-lg overflow-hidden">
        {/* Main button */}
        <button
          onClick={onDefaultClick}
          className={`
            inline-flex items-center font-medium transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900
            ${variants[variant]} ${sizes[size]}
            ${size === 'sm' ? 'px-2.5 py-1.5 gap-1.5' : 'px-3.5 py-2 gap-2'}
            ${variant === 'primary' ? 'focus:ring-emerald-500' : ''}
            ${variant === 'secondary' ? 'focus:ring-purple-500' : ''}
            ${variant === 'accent' ? 'focus:ring-orange-500' : ''}
            ${variant === 'info' ? 'focus:ring-blue-500' : ''}
            rounded-l-lg rounded-r-none
          `}
        >
          <Icon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} aria-hidden="true" />
          <span>{label}</span>
        </button>
        {/* Dropdown trigger */}
        <button
          onClick={handleDropdownClick}
          className={`
            inline-flex items-center justify-center font-medium transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900
            ${variants[variant]}
            ${size === 'sm' ? 'px-1.5 py-1.5' : 'px-2 py-2'}
            ${variant === 'primary' ? 'focus:ring-emerald-500' : ''}
            ${variant === 'secondary' ? 'focus:ring-purple-500' : ''}
            ${variant === 'accent' ? 'focus:ring-orange-500' : ''}
            ${variant === 'info' ? 'focus:ring-blue-500' : ''}
            rounded-r-lg rounded-l-none border-l ${dividerColors[variant]}
          `}
          aria-label="Select reorder strategy"
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          <ChevronDown className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} aria-hidden="true" />
        </button>
      </div>

      {/* Dropdown menu via portal */}
      {isOpen && typeof document !== 'undefined' &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setIsOpen(false)}
            />
            {/* Menu */}
            <div
              className="fixed w-64 py-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-[9999]"
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
              role="menu"
              aria-label={actions ? "Smart actions" : "Reorder strategies"}
            >
              {actions ? (
                // Smart actions menu
                actions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleActionClick(action.id)}
                    className="w-full px-4 py-2.5 text-left hover:bg-zinc-700 transition-colors"
                    role="menuitem"
                  >
                    <div className="text-sm font-medium text-zinc-200">{action.name}</div>
                    <div className="text-xs text-zinc-400">{action.description}</div>
                  </button>
                ))
              ) : (
                // Reorder strategies menu
                REORDER_STRATEGIES.map((strategy) => (
                  <button
                    key={strategy.id}
                    onClick={() => handleStrategyClick(strategy.id)}
                    className="w-full px-4 py-2.5 text-left hover:bg-zinc-700 transition-colors"
                    role="menuitem"
                  >
                    <div className="text-sm font-medium text-zinc-200">{strategy.name}</div>
                    <div className="text-xs text-zinc-400">{strategy.description}</div>
                  </button>
                ))
              )}
            </div>
          </>,
          document.body
        )}
    </>
  );
}

interface IconButtonProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  size?: 'sm' | 'md';
  onClick?: () => void;
  'aria-label': string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: 'menu' | 'dialog' | boolean;
}

function IconButton({
  icon: Icon,
  size = 'md',
  onClick,
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  'aria-haspopup': ariaHaspopup,
}: IconButtonProps) {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
  };

  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center justify-center rounded-lg
        bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors
        focus:outline-none focus:ring-2 focus:ring-zinc-500 focus:ring-offset-1 focus:ring-offset-zinc-900
        ${sizes[size]}
      `}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
    >
      <Icon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} aria-hidden="true" />
    </button>
  );
}

// Menu button with event-based positioning
interface MenuButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isExpanded: boolean;
  size?: 'sm' | 'md';
}

function MenuButton({ onClick, isExpanded, size = 'md' }: MenuButtonProps) {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
  };

  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center justify-center rounded-lg
        bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors
        focus:outline-none focus:ring-2 focus:ring-zinc-500 focus:ring-offset-1 focus:ring-offset-zinc-900
        ${sizes[size]}
      `}
      aria-label="More actions"
      aria-expanded={isExpanded}
      aria-haspopup="menu"
    >
      <MoreVertical className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} aria-hidden="true" />
    </button>
  );
}

// === Remove Artist Modal ===

interface RemoveArtistModalProps {
  tracks: TrackWithInstanceId[];
  onClose: () => void;
  onConfirm: (artistName: string) => void;
}

function RemoveArtistModal({ tracks, onClose, onConfirm }: RemoveArtistModalProps) {
  const [selectedArtist, setSelectedArtist] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Get all unique artists from tracks
  const allArtists = useMemo(() => {
    const artistMap = new Map<string, number>();
    tracks.forEach((track) => {
      track.artists.forEach((artist) => {
        const count = artistMap.get(artist.name) || 0;
        artistMap.set(artist.name, count + 1);
      });
    });

    // Convert to array and sort by track count (descending)
    return Array.from(artistMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [tracks]);

  // Filter artists based on search query
  const filteredArtists = useMemo(() => {
    if (!searchQuery) return allArtists;
    const query = searchQuery.toLowerCase();
    return allArtists.filter((artist) => artist.name.toLowerCase().includes(query));
  }, [allArtists, searchQuery]);

  const handleConfirm = () => {
    if (selectedArtist) {
      onConfirm(selectedArtist);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-zinc-900 rounded-lg shadow-2xl border border-zinc-800 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <UserX className="w-5 h-5 text-rose-500" />
            <h2 className="text-lg font-semibold text-zinc-100">Remove Artist</h2>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-zinc-400 mb-4">
            Select an artist to remove all their tracks from the playlist.
          </p>

          {/* Search */}
          <input
            type="text"
            placeholder="Search artists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 mb-4 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-rose-500"
          />

          {/* Artist List */}
          <div className="max-h-64 overflow-y-auto space-y-1 border border-zinc-800 rounded-lg">
            {filteredArtists.length === 0 ? (
              <div className="p-4 text-center text-zinc-500 text-sm">No artists found</div>
            ) : (
              filteredArtists.map((artist) => (
                <button
                  key={artist.name}
                  onClick={() => setSelectedArtist(artist.name)}
                  className={`
                    w-full flex items-center justify-between px-4 py-2.5 text-left text-sm transition-colors
                    ${
                      selectedArtist === artist.name
                        ? 'bg-rose-500/20 text-rose-300'
                        : 'text-zinc-300 hover:bg-zinc-800'
                    }
                  `}
                >
                  <span className="truncate">{artist.name}</span>
                  <span className="ml-2 text-xs text-zinc-500">{artist.count} tracks</span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-zinc-300 hover:text-zinc-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedArtist}
            className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <UserX className="w-4 h-4" />
            Remove Artist
          </button>
        </div>
      </div>
    </div>
  );
}

// === Utility Functions ===

function calculateAverageEnergy(
  tracks: Track[],
  audioFeaturesMap: Map<string, AudioFeatures>
): number | null {
  const energyValues = tracks
    .map((t) => audioFeaturesMap.get(t.id)?.energy)
    .filter((e): e is number => e !== undefined);

  return energyValues.length > 0 ? energyValues.reduce((sum, e) => sum + e, 0) / energyValues.length : null;
}
